import { ReactNode } from 'react'

import { Box, Tooltip } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { EtaSource } from 'generated/graphql'
import { usePermission } from 'providers'

import { getEtaSourceMeta } from './etaSourceMeta'
import { EtaSourceTooltipContent } from './EtaSourceTooltipContent'

interface IProps {
  source?: EtaSource | null
  lastUpdatedAt?: string | null
  hasValue: boolean
  tabIndex?: number
  children: ReactNode
}

export const EtaSourceTooltip = ({
  source,
  lastUpdatedAt,
  hasValue,
  tabIndex = 0,
  children,
}: IProps) => {
  const [isAllowedToViewEtaSource] = usePermission('ViewBookingsList')
  const { Icon, getColor, label } = getEtaSourceMeta(source)

  if (!isAllowedToViewEtaSource) return <>{children}</>

  return (
    <Tooltip
      arrow
      placement='top'
      enterDelay={150}
      title={
        <EtaSourceTooltipContent
          source={source}
          lastUpdatedAt={lastUpdatedAt}
          hasValue={hasValue}
        />
      }
      slotProps={{
        tooltip: {
          sx: {
            p: 0,
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1.5,
            boxShadow: (theme) => theme.shadows[6],
          },
        },
        arrow: {
          sx: {
            color: 'background.paper',
            '&::before': { border: 1, borderColor: 'divider' },
          },
        },
      }}>
      <Box
        component='span'
        tabIndex={tabIndex}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          maxWidth: '100%',
          minWidth: 0,
          cursor: 'default',
        }}>
        <Box
          component='span'
          sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {children}
        </Box>
        <Box
          component='span'
          role='img'
          aria-label={label}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: '50%',
            flexShrink: 0,
            color: getColor,
            bgcolor: (theme) => alpha(getColor(theme), 0.12),
          }}>
          <Icon sx={{ fontSize: 14 }} />
        </Box>
      </Box>
    </Tooltip>
  )
}
